import { FC, Fragment, useCallback } from "react";
import { useMatch, useNavigate, useResolvedPath } from "react-router-dom";
import {
  Chip,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Tooltip,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { NavItemProps } from "../NavItem";
import { useNavigationOverlayContext } from "../utils/useNavigationOverlay.context";
import { useHover } from "../../../../../utils/hooks/useHover.hook";
import { TypographyWithTooltip } from "../../../../common/TypographyWithTooltip";
import { useAppSelector } from "../../../../../store/hooks";
import { isSubscriptionDisabledSelector } from "../../../../../store/subscriptions/subscriptionsSelectors";

interface NavItemExpandedProps extends NavItemProps {}

export const NavItemExpanded: FC<NavItemExpandedProps> = ({
  primary,
  to,
  icon: Icon,
  selected,
  disabled,
  secondaryAction,
  secondaryActionHover,
  nested,
  iconProps,
  expandable,
  primaryTypographyProps,
  onButtonClick,
  expanded,
  comingSoon,
  listItemButtonSx,
  tooltipTitle,
}) => {
  const { ref, hovered } = useHover();
  const resolvedPath = useResolvedPath(to || "");
  const match = useMatch({ path: resolvedPath.pathname });
  const navigate = useNavigate();

  selected = (resolvedPath.pathname !== "/" && !!match) || selected;

  const isSubscriptionDisabled = useAppSelector(isSubscriptionDisabledSelector);

  disabled = disabled || isSubscriptionDisabled;

  const { onNavigate } = useNavigationOverlayContext();

  const handleOnClick = useCallback(() => {
    if (to) {
      navigate(to);
    }

    onNavigate?.();
  }, [to, navigate, onNavigate]);

  const buttonProps = {
    onClick: to ? handleOnClick : onButtonClick,
  };

  const navItemContent = (
    <Fragment>
      <ListItemIcon sx={listItemIconSx}>
        {expandable && hovered && onButtonClick && !disabled ? (
          <ArrowDropDownIcon
            sx={{ transform: expanded ? "rotate(0deg)" : "rotate(-90deg)" }}
          />
        ) : (
          Icon && (
            <Icon
              fontSize="small"
              color={selected ? "primary" : undefined}
              {...iconProps}
            />
          )
        )}
      </ListItemIcon>

      <ListItemText
        primary={
          <Stack direction="row" alignItems="center" spacing={1}>
            <TypographyWithTooltip title={primary} variant="inherit" />
            {comingSoon && (
              <Chip
                variant="filled"
                color="success"
                label="Coming Soon"
                size="small"
                sx={{
                  fontSize: 10,
                  height: 16,
                  borderRadius: 1,
                }}
              />
            )}
          </Stack>
        }
        primaryTypographyProps={{
          ...primaryTypographyPropsOverride,
          color: selected ? "primary" : undefined,
          ...primaryTypographyProps,
        }}
      />
    </Fragment>
  );

  return (
    <Tooltip arrow placement="top" title={tooltipTitle}>
      <ListItem
        sx={{
          ...listItemSx,
          ...(secondaryActionHover ? listItemSecondaryActionHoverable : {}),
        }}
        secondaryAction={secondaryAction}
        ref={ref}
      >
        {onButtonClick ? (
          <ListItemButton
            {...buttonProps}
            selected={expandable ? !expanded && selected : selected}
            disabled={disabled}
            sx={{
              pl: nested ? 3 : 1,
              borderRadius: 2,
              ...listItemButtonSx,
            }}
          >
            {navItemContent}
          </ListItemButton>
        ) : (
          navItemContent
        )}
      </ListItem>
    </Tooltip>
  );
};

const primaryTypographyPropsOverride = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const listItemSx = { px: 1, py: 0.25 };

const listItemSecondaryActionHoverable = {
  ".MuiListItemSecondaryAction-root": {
    visibility: "hidden",
  },
  "&:hover": {
    ".MuiListItemSecondaryAction-root": {
      visibility: "visible",
    },
  },
};

const listItemIconSx = {
  minWidth: 32,
};
